import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { NAVIGATE_TO } from '@routes'
import { observer } from 'mobx-react-lite'

import {
  AcAlert,
  AcButton,
  AcColumn,
  AcFormProvider,
  AcOrderDayCard,
  AcOrderFooter,
  AcRow,
} from '@components'
import {
  DIALOGS,
  LABELS,
  MINIMUM_LUNCHER_COUNT,
  WEEKDAYS_KEYS,
} from '@constants'
import { getDateRange } from '@helpers'
import { useStore } from '@hooks'
import { IOrderLunchersPayload } from '@typings'

interface IFormInputs {
  weekIdentifier: string
}
const FormInputs = observer(({ weekIdentifier }: IFormInputs) => {
  if (!weekIdentifier) {
    return null
  }

  const [week, year] = weekIdentifier.split('-')

  const { weeks, dialog, currentOrder } = useStore()
  const { watch } = useFormContext()
  const formValues = watch(Object.values(WEEKDAYS_KEYS))

  const totalLunchers = useMemo(() => {
    return Object.values(formValues).reduce((acc, current) => {
      return parseInt(acc) + parseInt(current || '0')
    })
  }, [formValues])

  return (
    <AcColumn gap={4}>
      <AcColumn gap={1}>
        {weeks.weekLunchers?.map((weekDay: any) => (
          <AcOrderDayCard
            weekIdentifier={weekIdentifier}
            weekDay={weekDay}
            disabled={!weeks.current?.days?.includes(weekDay.day)}
          />
        ))}
      </AcColumn>

      {totalLunchers < MINIMUM_LUNCHER_COUNT && (
        <AcAlert severity="warning">
          {LABELS.INSUFFICIENT_LUNCHER_COUNT}
        </AcAlert>
      )}

      <AcRow>
        <AcButton
          variant="secondary"
          onClick={() =>
            dialog.open({
              ...DIALOGS.NO_LUNCH_THIS_WEEK(weekIdentifier),
              subTitle: `Week ${week} - ${getDateRange(parseInt(week), parseInt(year))}`,
            })
          }>
          {LABELS.NO_LUNCH_THIS_WEEK}
        </AcButton>
      </AcRow>

      <AcOrderFooter loading={currentOrder.watchLoadingSubmitting} />
    </AcColumn>
  )
})

export const AcOrderLunchersForm = observer(
  ({
    weekIdentifier,
    orderId,
  }: {
    weekIdentifier: string
    orderId?: string
  }) => {
    const { currentOrder, toaster } = useStore()
    const navigate = useNavigate()

    const handleSubmit = async (body: IOrderLunchersPayload) => {
      const payload = { ...body }
      Object.entries(body).forEach(([key, value]) => {
        if (value === '') {
          payload[key as keyof typeof payload] = '0'
        }
      })

      try {
        const isOrderEdit = Boolean(currentOrder.currentOrderId)
        const res = await currentOrder.submitLuncherCount(payload, isOrderEdit)
        if (res) {
          orderId
            ? navigate(
                NAVIGATE_TO.EDIT_ORDER_PREFERENCES(weekIdentifier, orderId),
              )
            : navigate(NAVIGATE_TO.ORDER_PREFERENCES(weekIdentifier))
        }
      } catch (error: any) {
        if (error.type === 'axios') {
          // setErrors(error.errorFields)
        }
        toaster.error({
          title: 'Nog niet alle velden zijn juist ingevuld',
          content: error.message,
        })
      }
    }

    if (!weekIdentifier) {
      return null
    }

    const initialFormData = async () => {
      return await currentOrder.initFirstStepFormValues(weekIdentifier, orderId)
    }

    return (
      <AcFormProvider
        initial={initialFormData}
        onSubmit={handleSubmit}>
        <FormInputs weekIdentifier={weekIdentifier} />
      </AcFormProvider>
    )
  },
)
